import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { PhaseHeader } from "./phase-header";
import { PhaseContent } from "./phase-content";
import { Collapse, Space, Button, Spin } from "antd";
import { ThunderboltOutlined, EditOutlined } from "@ant-design/icons";
import {
  Phase,
  Task,
  Work,
  PricingLevel,
  PricingType,
  OnPhasePriceChange,
  PhaseBase,
} from "../../data-types";
import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableItem, DraggableHandle } from "../draggable-list";
import styles from "./phase-list.css";
import { BEDROCK_GET_PHASES_AND_TASKS_QUERY } from "~/graphql/query";
import { useLazyQuery } from "@apollo/client";
import { Userpilot } from "userpilot";
import { TeamMembershipPartsFragment } from "~/gql/graphql";
import { TimelineEditContext } from "../matter-relation-editor/scope/timeline-modal";

const {
  panel: panelClassName,
  space: spaceClassName,
  button: buttonClassName,
  "button-container": buttonContainerClassName,
  "primary-button": primaryButtonClassName,
  "custom-button": customButtonClassName,
  "loading-container": loadingContainerClassName,
  "loading-messages": loadingMessagesClassName,
  "loading-message": loadingMessageClassName,
  "loading-spinner": loadingSpinnerClassName,
  "refine-overlay": refineOverlayClassName,
  "refine-content": refineContentClassName,
  "refine-message": refineMessageClassName,
} = styles as any;

export type PhaseListHandle = {
  handleAutoGenerate: (
    useLibraryData: boolean,
    refineMode: boolean,
    customPrompt: string | null
  ) => void;
};

type Props = {
  matterId: number;
  template?: boolean;
  title?: string;
  phases: Phase[] | null;
  onUpdate: (phase: PhaseBase) => void;
  onDelete: (id: number) => void;
  onCreatePhases: (phases: { phase: string; tasks: string[] }[]) => void;
  onUpdateTask: (task: Partial<Task>, phaseId: number) => void;
  onDeleteTask: (taskId: number, phaseId: number) => void;
  onCreateTask: (phaseId: number) => void;
  onCreateTasks: (taskNames: string[], phaseId: number) => void;
  onUpdateFeeEarner: (work: Work, phaseId: number, timeEntryUpdate: boolean) => void;
  onDeleteFeeEarner: (id: number, phaseId: number) => void;
  onCreateFeeEarner: (phaseId: number) => void;
  showPriceRangeInput?: boolean;
  pricingLevel: PricingLevel;
  pricingType: PricingType;
  onPriceChange: OnPhasePriceChange;
  onPhaseReorder: (activeId: number, overId: number) => void;
  onTaskReorder: (activeId: number, overId: number) => void;
  onAutoFocus: () => void;
  teamMembers: TeamMembershipPartsFragment[];
  currencySymbol: string;
  countryCode?: string;
  useAi?: boolean;
  expandedPanels: string[];
  setExpandedPanels: (expandedPanels: string[]) => void;
  defaultFont?: string;
  setCustomModalVisible: (visible: boolean) => void;
  onTimelineEdit: (context: TimelineEditContext) => void;
};

const PhaseList = forwardRef<PhaseListHandle, Props>(
  (
    {
      matterId,
      template,
      title,
      phases,
      onDelete,
      onUpdate,
      onCreatePhases,
      onUpdateTask,
      onCreateTask,
      onCreateTasks,
      onDeleteTask,
      onDeleteFeeEarner,
      onUpdateFeeEarner,
      onCreateFeeEarner,
      pricingLevel,
      pricingType,
      onPriceChange,
      onPhaseReorder,
      onTaskReorder,
      onAutoFocus,
      teamMembers,
      currencySymbol,
      countryCode,
      useAi,
      expandedPanels,
      setExpandedPanels,
      defaultFont,
      setCustomModalVisible,
      onTimelineEdit,
    },
    ref
  ) => {
    const sensors = useSensors(useSensor(PointerSensor));
    const [activePhase, setActivePhase] = useState<Phase | null>(null);
    const [phasesAndTasksVersion, setPhasesAndTasksVersion] = useState(0);
    const [isRefining, setIsRefining] = useState(false);

    const [getPhasesAndTasks, { data, loading }] = useLazyQuery(
      BEDROCK_GET_PHASES_AND_TASKS_QUERY,
      {
        variables: {
          matterId,
          useLibraryData: true,
          _version: phasesAndTasksVersion,
          customPrompt: null,
          countryCode,
        } as any,
      }
    );

    const [loadingMessages, setLoadingMessages] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);

    const messages = [
      { text: "🔍 Analysing your matter...", delay: 1500 },
      { text: "📋 Drafting relevant phases...", delay: 2000 },
      { text: "✨ Crafting tailored tasks...", delay: 2500 },
      { text: "🎯 Finalising scope...", delay: 2000 },
    ];

    useEffect(() => {
      if (loading && !isRefining) {
        setIsProcessing(true);
        let totalDelay = 0;
        const timeouts: NodeJS.Timeout[] = [];

        messages.forEach((message, index) => {
          totalDelay += index === 0 ? message.delay : messages[index - 1].delay;
          const timeout = setTimeout(() => {
            setLoadingMessages((prevMessages) => [...prevMessages, message.text]);
          }, totalDelay);

          timeouts.push(timeout);
        });

        return () => {
          timeouts.forEach(clearTimeout);
        };
      }
    }, [loading, isRefining]);

    useEffect(() => {
      if (data?.bedrockGetPhasesAndTasks && data?.bedrockGetPhasesAndTasks.length > 0) {
        onCreatePhases(
          data.bedrockGetPhasesAndTasks.map((p) => ({ phase: p.phaseTitle, tasks: p.tasks }))
        );
        setTimeout(() => {
          setLoadingMessages([]);
          setIsProcessing(false);
          setIsRefining(false);
        }, 500);
      }
    }, [data?.bedrockGetPhasesAndTasks]);

    const handleAutoGenerate = async (
      useLibraryData: boolean,
      refineMode: boolean,
      customPrompt: string | null = null
    ) => {
      if (useAi && title) {
        setPhasesAndTasksVersion(phasesAndTasksVersion + 1);

        // Set refining state if in refine mode and phases exist
        if (refineMode && phases && phases.length > 0) {
          setIsRefining(true);
        }

        getPhasesAndTasks({
          variables: {
            matterId,
            useLibraryData,
            _version: phasesAndTasksVersion,
            customPrompt,
            refineMode,
            countryCode,
          } as any,
          fetchPolicy: "network-only",
        });
      }
    };

    useImperativeHandle(ref, () => ({
      handleAutoGenerate,
    }));

    return (
      <>
        {isRefining && (
          <div className={refineOverlayClassName}>
            <div className={refineContentClassName}>
              <div className={refineMessageClassName}>Refining scope...</div>
              <Spin className={loadingSpinnerClassName} size="large" />
            </div>
          </div>
        )}
        <DndContext
          onDragStart={({ active }) => {
            const activePhase = phases?.find((p) => p.id.toString() === active.id)!;
            activePhase.autoFocus = false;
            setActivePhase(activePhase);
          }}
          onDragEnd={({ active, over }) =>
            over && onPhaseReorder(parseInt(active.id), parseInt(over.id))
          }
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
        >
          <div style={{ position: "relative" }}>
            <SortableContext
              items={phases?.map((p) => p.id.toString()) ?? []}
              strategy={verticalListSortingStrategy}
            >
              <Space direction="vertical" className={spaceClassName}>
                {phases?.map((phase) => {
                  const shouldAutoFocus = phase?.autoFocus || false;
                  return (
                    <SortableItem id={phase.id.toString()} key={phase.id}>
                      {({ attributes, listeners }) => (
                        <Collapse
                          collapsible="icon"
                          expandIconPosition="right"
                          activeKey={expandedPanels}
                          onChange={(keys) =>
                            setExpandedPanels(typeof keys === "string" ? [keys] : keys)
                          }
                          key={phase.id.toString()}
                        >
                          <Collapse.Panel
                            className={panelClassName}
                            header={
                              <PhaseHeader<typeof shouldAutoFocus>
                                currencySymbol={currencySymbol}
                                onAutoFocus={onAutoFocus}
                                autoFocus={shouldAutoFocus}
                                template={template}
                                phase={phase}
                                onUpdate={onUpdate}
                                onDelete={onDelete}
                                pricingLevel={pricingLevel}
                                pricingType={pricingType}
                                onPriceChange={onPriceChange}
                                defaultFont={defaultFont}
                                prefix={<DraggableHandle {...attributes} {...listeners} />}
                                onTimelineEdit={(context) =>
                                  onTimelineEdit({
                                    ...context,
                                    type: "phase",
                                    phaseId: phase.id,
                                  })
                                }
                              />
                            }
                            key={phase.id}
                            forceRender
                          >
                            <PhaseContent
                              matterId={matterId}
                              currencySymbol={currencySymbol}
                              proposalTitle={title}
                              template={template}
                              phase={phase}
                              onDelete={onDelete}
                              onUpdate={onUpdate}
                              onCopyFromPreviousPhase={() => console.log("")}
                              onUpdateTask={(task) => onUpdateTask(task, phase.id)}
                              onCreateTask={() => onCreateTask(phase.id)}
                              onCreateTasks={async (taskNames) =>
                                onCreateTasks(taskNames, phase.id)
                              }
                              onDeleteTask={(taskId) => onDeleteTask(taskId, phase.id)}
                              onUpdateFeeEarner={(work, timeEntryUpdate) =>
                                onUpdateFeeEarner(work, phase.id, timeEntryUpdate)
                              }
                              onDeleteFeeEarner={(id) => onDeleteFeeEarner(id, phase.id)}
                              onCreateFeeEarner={() => onCreateFeeEarner(phase.id)}
                              showFeeEarners={pricingLevel === PricingLevel.FeeEarner}
                              onReorderTasks={onTaskReorder}
                              pricingType={pricingType}
                              teamMembers={teamMembers}
                              useAi={useAi}
                              countryCode={countryCode}
                              onTimelineEdit={(context) =>
                                onTimelineEdit({ ...context, type: "task", phaseId: phase.id })
                              }
                            />
                          </Collapse.Panel>
                        </Collapse>
                      )}
                    </SortableItem>
                  );
                })}
              </Space>
            </SortableContext>
          </div>

          {useAi && (!phases || phases.length === 0) && (
            <>
              {(loading || isProcessing) && !isRefining ? (
                <div className={loadingContainerClassName}>
                  <div className={loadingMessagesClassName}>
                    {loadingMessages.map((msg, index) => (
                      <div key={index} className={loadingMessageClassName}>
                        {msg}
                      </div>
                    ))}
                  </div>
                  <Spin className={loadingSpinnerClassName} size="large" />
                </div>
              ) : (
                <Space>
                  <div className={buttonContainerClassName}>
                    <Button
                      className={`${buttonClassName} ${primaryButtonClassName}`}
                      icon={<ThunderboltOutlined />}
                      size="large"
                      type="primary"
                      onClick={() => {
                        Userpilot.track("ai_first_draft");
                        return handleAutoGenerate(true, false, null);
                      }}
                    >
                      First Draft
                    </Button>
                    <Button
                      className={`${buttonClassName} ${customButtonClassName}`}
                      icon={<EditOutlined />}
                      size="large"
                      type="default"
                      onClick={() => setCustomModalVisible(true)}
                    >
                      Customise
                    </Button>
                  </div>
                </Space>
              )}
            </>
          )}

          <DragOverlay>
            {activePhase ? (
              <Collapse expandIconPosition="right">
                <Collapse.Panel
                  key={activePhase.id}
                  className={panelClassName}
                  header={
                    <PhaseHeader<false>
                      currencySymbol={currencySymbol}
                      autoFocus={false}
                      dragging={true}
                      template={template}
                      phase={activePhase}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      pricingLevel={pricingLevel}
                      pricingType={pricingType}
                      onPriceChange={onPriceChange}
                      defaultFont={defaultFont}
                      prefix={<DraggableHandle />}
                      onTimelineEdit={(context) =>
                        onTimelineEdit({ ...context, type: "phase", phaseId: activePhase.id })
                      }
                    />
                  }
                  forceRender
                >
                  {/* Drag overlay content intentionally empty */}
                </Collapse.Panel>
              </Collapse>
            ) : null}
          </DragOverlay>
        </DndContext>
      </>
    );
  }
);

PhaseList.displayName = "PhaseList";

export default PhaseList;
